import { ANGLE_STEPS, iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { cellAtWorld, cellCenter, findPath, heroWalkable } from '../grid.ts';
import type { BarrenaState, BattleState, DrillsRules, DrillsState, Enemy } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { inArc, turnOf } from './golems.ts';

/**
 * La Barrena (docs/sendas/artifice.md, chapter 4, act 3, the boss of the test): a nightmare of ice and old iron that put
 * itself together out of the mine's broken drills. It comes out of their heap at `out` and goes for a drill in and
 * turning (the nearest); with the hero within `chase` (or for a while after it stopped a drill, `stall`) it goes for
 * the hero instead; with no drill turning, for the hero too. It turns slowly; it walks only forward. Its slash: the
 * augers lifted `warn` (3 s, its aim held), then an arc in front of it (`heroBp` of the hero's life, `blows` of a
 * person's). A drill it reaches (`stallR`) stops `stall` (8 s) and that wall's cut starts over. Its spin (`spin`,
 * warned 3 s) throws ice through the whole hall, nowhere out of it: its fight can't be long. No side of it is covered.
 *
 * An ogre in the sim, drawn as La Barrena (the render reads `s.senda.drills.bz`); this module moves it (`barrenaHold`,
 * from the enemies' tick).
 *
 * Cost: one boss; it looks at the hero and four drills once a tick; a way round the block once a second at most.
 */

const norm = (a: number): number => ((a % ANGLE_STEPS) + ANGLE_STEPS) % ANGLE_STEPS;

export const barrenaFresh = (r: DrillsRules): BarrenaState => ({ id: 0, mode: 0, wind: -1, aim: 0, next: 0, again: 0, spin: r.boss.spin.first, spinAt: -1, step: -1, stepAt: -100, pend: [], down: -1 });

/** Whether an enemy is La Barrena. */
export const barrenaOf = (s: BattleState, id: number): boolean => !!id && s.senda?.drills?.bz.id === id;

/** Its coming out, its spin, its fall (from the act's tick). */
export const barrenaTick = (s: BattleState, r: DrillsRules, w: DrillsState, cue: (c: string) => void): void => {
  const B = r.boss;
  const bz = w.bz;
  if (bz.pend.length) {
    for (const c of bz.pend) cue(c);
    bz.pend.length = 0;
  }
  if (bz.mode === 3) return;
  if (bz.mode === 0) {
    if (s.waveT < B.out) return;
    const e = spawnEnemy(s, 'ogre', 1, false, 0, B.at.x, B.at.y, B.hp);
    e.facing = iatan2(-e.y, -e.x);
    e.anim = 'idle';
    bz.id = e.id;
    bz.mode = 1;
    bz.next = s.tick + 24;
    ev(s, { t: 'senda', kind: 'bzOut', npc: '', x: e.x, y: e.y });
    cue('out');
    return;
  }
  const e = s.enemies.find((x) => x.id === bz.id);
  if (!e || e.hp <= 0) {
    bz.mode = 3;
    bz.down = s.tick;
    bz.wind = -1;
    bz.spinAt = -1;
    ev(s, { t: 'senda', kind: 'bzDown', npc: '', x: e?.x ?? B.at.x, y: e?.y ?? B.at.y });
    cue('bossDown');
    return;
  }
  if (bz.spinAt < 0 && s.waveT >= bz.spin) {
    bz.spinAt = s.tick + B.spin.warn;
    ev(s, { t: 'senda', kind: 'bzSpinWarn', npc: '', x: e.x, y: e.y, n: B.spin.warn });
    cue('spin');
  } else if (bz.spinAt >= 0 && s.tick >= bz.spinAt) {
    bz.spinAt = -1;
    bz.spin = s.waveT + B.spin.every - B.spin.warn;
    ev(s, { t: 'senda', kind: 'bzSpin', npc: '', x: e.x, y: e.y });
    for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, B.spin.heroBp), e, 'frost', true);
    if (B.spin.blows > 0)
      for (const n of s.senda?.npcs ?? []) {
        if (n.hp <= 0 || n.away) continue;
        n.hp = imax(0, n.hp - B.spin.blows);
        ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
      }
  }
};

const turnToward = (e: Enemy, to: number, turn: number): void => {
  const d = turnOf(e.facing, to);
  e.facing = norm(e.facing + (d > turn ? turn : d < -turn ? -turn : d));
};

const stepTo = (s: BattleState, bz: BarrenaState, e: Enemy, x: number, y: number): { x: number; y: number } => {
  const dx = x - e.x;
  const dy = y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  if (heroWalkable(s, cellAtWorld(s, e.x + idiv(dx * 700, d), e.y + idiv(dy * 700, d)))) return { x, y };
  if (s.tick - bz.stepAt >= 12 || bz.step < 0) {
    bz.stepAt = s.tick;
    const way = findPath(s, cellAtWorld(s, e.x, e.y), cellAtWorld(s, x, y));
    bz.step = way[0] ?? -1;
  }
  return bz.step >= 0 ? cellCenter(s.map.radius, bz.step) : { x, y };
};

/** The nearest drill in and turning (−1: none). */
const drillFor = (s: BattleState, r: DrillsRules, w: DrillsState, e: Enemy): number => {
  let best = -1;
  let bd = 0x7fffffff;
  r.drills.forEach((x, d) => {
    if (!w.inn[d] || s.tick < (w.stall[d] as number) || (w.down[x.wall] as number) >= 0) return;
    const dd = (x.at.x - e.x) * (x.at.x - e.x) + (x.at.y - e.y) * (x.at.y - e.y);
    if (dd < bd) {
      bd = dd;
      best = d;
    }
  });
  return best;
};

const slash = (s: BattleState, r: DrillsRules, bz: BarrenaState, e: Enemy): void => {
  const B = r.boss;
  ev(s, { t: 'senda', kind: 'bzSlash', npc: '', x: e.x, y: e.y, n: bz.aim });
  e.anim = 'attack';
  e.animT = 0;
  for (const h of s.heroes) if (h.downTicks === 0 && inArc(e.x, e.y, bz.aim, B.reach, B.arc, h.x, h.y)) damageHero(s, h, mulBp(h.stats.maxHp, B.heroBp), e, 'physical', true);
  for (const n of s.senda?.npcs ?? []) {
    if (n.hp <= 0 || n.away || !inArc(e.x, e.y, bz.aim, B.reach, B.arc, n.x, n.y)) continue;
    n.hp = imax(0, n.hp - B.blows);
    n.flinch = 12;
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/** La Barrena's move (from the enemies' tick; true: it's the boss, nothing else moves it). */
export const barrenaHold = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.drills;
  const w = s.senda?.drills;
  if (!r || !w || w.bz.id !== e.id) return false;
  const bz = w.bz;
  const B = r.boss;
  if (bz.mode === 3) return true;
  if (bz.wind >= 0) {
    e.facing = bz.aim;
    e.anim = 'cast';
    if (s.tick >= bz.wind) {
      slash(s, r, bz, e);
      bz.wind = -1;
      bz.next = s.tick + B.rest;
    }
    return true;
  }
  if (e.anim === 'attack' && e.animT > 8) e.anim = 'idle';
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h && s.tick >= bz.next && inArc(e.x, e.y, e.facing, B.reach - 150, 110, h.x, h.y)) {
    bz.wind = s.tick + B.warn;
    bz.aim = e.facing;
    e.anim = 'cast';
    e.animT = 0;
    ev(s, { t: 'senda', kind: 'bzWarn', npc: '', x: e.x, y: e.y, n: bz.aim });
    return true;
  }
  const d = drillFor(s, r, w, e);
  const chase = !!h && (d < 0 || s.tick < bz.again || withinRange(e.x, e.y, h.x, h.y, B.chase));
  const dr = d >= 0 ? r.drills[d] : undefined;
  if (!chase && dr && withinRange(e.x, e.y, dr.at.x, dr.at.y, B.stallR)) {
    w.stall[d] = s.tick + B.stall;
    w.cut[dr.wall] = 0;
    bz.again = s.tick + B.stall;
    ev(s, { t: 'senda', kind: 'drillStall', npc: '', x: dr.at.x, y: dr.at.y, n: d });
    bz.pend.push('stall');
    return true;
  }
  const to = chase && h ? { x: h.x, y: h.y } : dr ? dr.at : null;
  if (!to) {
    e.anim = 'idle';
    return true;
  }
  const way = stepTo(s, bz, e, to.x, to.y);
  const want = iatan2(way.y - e.y, way.x - e.x);
  turnToward(e, want, B.turn);
  const off = turnOf(e.facing, want);
  if (chase && h && withinRange(e.x, e.y, h.x, h.y, B.reach - 300)) {
    e.anim = 'idle';
    return true;
  }
  if (off > 170 || off < -170) {
    e.anim = 'idle';
    return true;
  }
  const dx = way.x - e.x;
  const dy = way.y - e.y;
  const dd = imax(1, isqrt(dx * dx + dy * dy));
  const nx = e.x + idiv(dx * B.pace, dd);
  const ny = e.y + idiv(dy * B.pace, dd);
  if (heroWalkable(s, cellAtWorld(s, nx, ny))) {
    e.x = nx;
    e.y = ny;
  }
  e.anim = 'walk';
  return true;
};
